import { AlarmClock, Check, Copy, History, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import type { ControllerMode, ScheduleEntry } from "@/data/types";
import { MODE_CATALOG, defaultParamsFor } from "@/data/modeCatalog";
import { NumberField } from "@/components/ui/NumberField";
import { HoldToConfirm } from "@/components/ui/HoldToConfirm";
import { trapTabKey } from "@/components/CommandPalette";
import { carryTo } from "@/lib/carryParams";
import { cn } from "@/lib/cn";
import { useT } from "@/lib/i18n";
import { DAYS_SHORT, MINUTES_IN_DAY, fmtHm, projectInTz } from "./scheduleLib";
import { ModePicker } from "./ModePicker";
import { ParamsEditor } from "./ParamsEditor";

/**
 * Most recent COMPLETED occurrence of an entry's window, evaluated in the
 * schedule's timezone (LINK-06 / MKT-10). Walks back day-by-day from `now`
 * (9 days covers any non-empty days_of_week plus a wrap margin) and returns
 * the first occurrence whose window already ended. Wraps-midnight windows
 * (end_min < start_min) span into the NEXT day, matching the resolver's
 * `entryCovers` semantics. Returns null when the entry can never fire.
 *
 * Lives here (not scheduleLib) only because scheduleLib is owned by the
 * resolver contract; this is a UI-side convenience shared with
 * ActiveStatePanel.
 */
export function occurrenceWindow(
  entry: Pick<ScheduleEntry, "days_of_week" | "start_min" | "end_min" | "date_start" | "date_end">,
  tz: string,
  now: number = Date.now(),
): { start: number; end: number } | null {
  if (!entry.days_of_week.length) return null;
  if (entry.start_min === entry.end_min) return null;   // zero-width never fires
  const spanMs =
    (((entry.end_min - entry.start_min + MINUTES_IN_DAY) % MINUTES_IN_DAY) || MINUTES_IN_DAY) * 60_000;
  const nowMin = now - (now % 60_000);
  const proj = projectInTz(tz, nowMin);
  // Midnight (in tz) of the day containing `now` — same origin trick as
  // scheduleLib's nextChangeAfter.
  const todayOrigin = nowMin - proj.minute * 60_000;
  for (let back = 0; back <= 8; back++) {
    const weekday = (proj.weekday - back + 70) % 7;
    if (!entry.days_of_week.includes(weekday)) continue;
    const origin = todayOrigin - back * 86_400_000;
    if (entry.date_start || entry.date_end) {
      // Date bounds use the occurrence-day's date in tz (midday probe
      // sidesteps DST-shifted midnights).
      const ymd = projectInTz(tz, origin + 12 * 3_600_000).ymd;
      if (entry.date_start && ymd < entry.date_start) continue;
      if (entry.date_end && ymd > entry.date_end) continue;
    }
    const start = origin + entry.start_min * 60_000;
    const end = start + spanMs;
    if (end <= now) return { start, end };
  }
  return null;
}

/** /historical deep-link for a computed occurrence window — curated keys
 *  only, so it works without the async device catalog (LINK-06). */
export function lastRunHistoricalHref(win: { start: number; end: number }): string {
  return carryTo("/historical", {
    keys: "grid_W,battery/aggregation/power",
    start: String(win.start),
    end: String(win.end),
    gran: "2m",
    preset: "custom",
  });
}

/**
 * Single-entry editor — modal drawer. Mutates a single ScheduleEntry; the
 * page composes a save when the user dismisses (or chooses Save).
 *
 * Every field the cloud-connector accepts on the wire is editable here:
 *   * id (read-only after first commit — wire dedup uses the id)
 *   * name (free text)
 *   * enabled toggle
 *   * priority (int; highest wins, ties alphabetical by id)
 *   * days_of_week (0=Mon..6=Sun multi-select)
 *   * time_start / time_end (HH:MM, with a special "End at 24:00" affordance)
 *   * date_start / date_end (optional inclusive bounds, YYYY-MM-DD)
 *   * mode (chip picker)
 *   * mode-specific params (via ParamsEditor)
 *
 * The modal closes on backdrop click and on ESC. Mutations stream live to
 * `onChange`; the page batches them into a save.
 */
export function EntryEditor({
  entry,
  timezone,
  isNew = false,
  dirty = false,
  onChange,
  onClone,
  onRemove,
  onSave,
  onClose,
}: {
  entry: ScheduleEntry;
  /** Schedule-doc timezone — drives the "last run" occurrence math. */
  timezone: string;
  /** True iff this entry hasn't been committed (the id may still be edited). */
  isNew?: boolean;
  /** Page-level dirty flag — surfaces the in-modal Hold-to-save (N12). */
  dirty?: boolean;
  onChange: (next: ScheduleEntry) => void;
  onClone: () => void;
  onRemove: () => void;
  /**
   * Persist the WHOLE draft doc (the page's saveDraft). Async — the editor
   * closes only after it resolves so the success toast / edge echo isn't
   * pre-empted (N12). Optional so callers that don't wire it keep working.
   */
  onSave?: () => Promise<void>;
  onClose: () => void;
}) {
  const t = useT();
  // ESC closes.
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);

  // Focus the name field on open for fast keyboard editing.
  const nameRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => { nameRef.current?.focus(); }, []);

  // Dialog semantics (XTRA-04): Tab wraps inside the card so keyboard focus
  // can't escape into the obscured schedule page behind the modal.
  const cardRef = useRef<HTMLDivElement | null>(null);

  const set = (patch: Partial<ScheduleEntry>) => onChange({ ...entry, ...patch });

  const setMode = (mode: ControllerMode) => {
    // Reset params on mode change so we never carry stale fields between
    // modes (e.g. import_max_W from PeakShaving to ConstantPower).
    onChange({ ...entry, mode, params: defaultParamsFor(mode) });
  };

  const toggleDay = (d: number) => {
    const next = entry.days_of_week.includes(d)
      ? entry.days_of_week.filter((x) => x !== d)
      : [...entry.days_of_week, d];
    set({ days_of_week: next.sort((a, b) => a - b) });
  };

  const numericParams = useMemo(() => {
    const out: Record<string, number> = {};
    for (const [k, v] of Object.entries(entry.params ?? {})) {
      const n = typeof v === "number" ? v : Number(v);
      out[k] = Number.isFinite(n) ? n : 0;
    }
    return out;
  }, [entry.params]);

  const wrap = entry.end_min !== 0 && entry.end_min <= entry.start_min;
  const zeroWidth = entry.start_min === entry.end_min;

  // Most recent completed occurrence — powers "View last run in
  // Historical" (LINK-06 / MKT-10). Null for brand-new entries (no past
  // run exists yet) and entries that can never fire.
  const lastRun = useMemo(
    () => (isNew ? null : occurrenceWindow(entry, timezone)),
    // Re-derive only on the fields that move the window.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isNew, timezone, entry.days_of_week.join(","), entry.start_min, entry.end_min, entry.date_start, entry.date_end],
  );

  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-black/55 p-2 md:items-center md:p-8"
      onClick={onClose}
    >
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-label={isNew ? t("manage.entry.ariaNew") : t("manage.entry.ariaEdit")}
        onKeyDown={(e) => trapTabKey(e, cardRef.current)}
        className="card w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-bg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="flex items-center justify-between border-b border-edge px-4 py-3">
          <div>
            <div className="text-xs font-semibold text-text-muted">
              {isNew ? t("manage.entry.new") : t("manage.entry.edit")}
            </div>
            <div className="font-mono text-[11px] text-text-faint">
              {entry.id}
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-md border border-edge p-1.5 text-text-muted hover:bg-surface-2"
            title={t("common.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* body */}
        <div className="space-y-4 p-4 text-sm">
          {/* id + name */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field label={t("manage.entry.id")}>
              <input
                value={entry.id}
                disabled={!isNew}
                onChange={(e) => set({ id: e.target.value.trim() })}
                className="text-input w-full rounded-md border border-edge bg-surface px-2 py-1.5 font-mono text-sm disabled:opacity-60"
              />
              {!isNew && (
                <div className="mt-1 text-[10px] text-text-faint">
                  {t("manage.entry.idLocked")}
                </div>
              )}
            </Field>
            <Field label={t("manage.entry.name")}>
              <input
                ref={nameRef}
                value={entry.name ?? ""}
                onChange={(e) => set({ name: e.target.value })}
                className="text-input w-full rounded-md border border-edge bg-surface px-2 py-1.5 text-sm"
                placeholder={t("manage.entry.unnamed")}
              />
            </Field>
          </div>

          {/* enabled + priority + mode */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <Field label={t("manage.entry.enabled")}>
              <BoolToggle value={entry.enabled} onChange={(v) => set({ enabled: v })} />
            </Field>
            <Field
              label={t("manage.entry.priority")}
              hint={t("manage.entry.priorityHint")}
            >
              <NumberField
                value={entry.priority}
                onChange={(v) => set({ priority: Math.round(v) })}
                allowSigned
                className="w-28"
              />
            </Field>
            <Field label={t("manage.override.mode")}>
              <ModePicker value={entry.mode} onChange={setMode} />
            </Field>
          </div>

          {/* days of week */}
          <Field label={t("manage.entry.daysOfWeek")}>
            <div className="flex flex-wrap items-center gap-1">
              {DAYS_SHORT.map((d, i) => {
                const on = entry.days_of_week.includes(i);
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => toggleDay(i)}
                    className={cn(
                      "h-8 w-12 rounded-md border text-xs font-medium",
                      on
                        ? "border-accent bg-accent/15 text-accent"
                        : "border-edge text-text-muted hover:border-edge-strong",
                    )}
                  >
                    {d}
                  </button>
                );
              })}
              <div className="ml-3 flex gap-1">
                <button
                  type="button"
                  onClick={() => set({ days_of_week: [0, 1, 2, 3, 4] })}
                  className="h-8 rounded-md border border-edge px-2 text-[11px] text-text-muted hover:bg-surface-2"
                >
                  {t("manage.entry.weekdays")}
                </button>
                <button
                  type="button"
                  onClick={() => set({ days_of_week: [5, 6] })}
                  className="h-8 rounded-md border border-edge px-2 text-[11px] text-text-muted hover:bg-surface-2"
                >
                  {t("manage.entry.weekend")}
                </button>
                <button
                  type="button"
                  onClick={() => set({ days_of_week: [0, 1, 2, 3, 4, 5, 6] })}
                  className="h-8 rounded-md border border-edge px-2 text-[11px] text-text-muted hover:bg-surface-2"
                >
                  {t("manage.entry.everyDay")}
                </button>
              </div>
            </div>
          </Field>

          {/* time window */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <Field label={t("manage.entry.start")}>
              <TimeInput value={entry.start_min} onChange={(v) => set({ start_min: v })} />
            </Field>
            <Field label={t("manage.entry.end")}>
              <TimeInput value={entry.end_min} onChange={(v) => set({ end_min: v })} allowEndOfDay />
            </Field>
            <Field label={t("manage.entry.shift")}>
              <div className="flex gap-1">
                {[-60, -15, 15, 60].map((delta) => (
                  <button
                    key={delta}
                    type="button"
                    onClick={() => {
                      const span = (entry.end_min - entry.start_min + 1440) % 1440 || 1440;
                      const nextStart = (entry.start_min + delta + 1440) % 1440;
                      const sum = nextStart + span;
                      const nextEnd = entry.end_min === 1440
                        ? Math.min(1440, sum)
                        : sum > 1440 ? sum - 1440 : sum;
                      set({ start_min: nextStart, end_min: nextEnd });
                    }}
                    className="h-8 min-w-[42px] rounded-md border border-edge px-1.5 text-[11px] text-text-muted hover:bg-surface-2"
                  >
                    {delta > 0 ? `+${delta}m` : `${delta}m`}
                  </button>
                ))}
              </div>
            </Field>
          </div>
          {zeroWidth && (
            <div className="flex items-center gap-1 text-[11px] text-critical">
              <AlarmClock className="h-3 w-3" /> {t("manage.entry.zeroWidth")}
            </div>
          )}
          {wrap && !zeroWidth && (
            <div className="flex items-center gap-1 text-[11px] text-warn">
              <AlarmClock className="h-3 w-3" /> {t("manage.entry.wraps", { start: fmtHm(entry.start_min), end: fmtHm(entry.end_min) })}
            </div>
          )}

          {/* optional date range */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field
              label={t("manage.entry.dateStart")}
              hint={t("manage.entry.dateStartHint")}
            >
              <input
                type="date"
                value={entry.date_start ?? ""}
                onChange={(e) => set({ date_start: e.target.value || null })}
                className="text-input w-full rounded-md border border-edge bg-surface px-2 py-1.5 text-sm"
              />
            </Field>
            <Field
              label={t("manage.entry.dateEnd")}
              hint={t("manage.entry.dateEndHint")}
            >
              <input
                type="date"
                value={entry.date_end ?? ""}
                onChange={(e) => set({ date_end: e.target.value || null })}
                className="text-input w-full rounded-md border border-edge bg-surface px-2 py-1.5 text-sm"
              />
            </Field>
          </div>

          {/* mode parameters */}
          <div>
            <div className="mb-1 text-[10px] uppercase tracking-wider text-text-faint font-semibold">
              {t("manage.entry.modeParams", { mode: MODE_CATALOG[entry.mode].label })}
            </div>
            <ParamsEditor
              mode={entry.mode}
              value={numericParams}
              onChange={(next) => set({ params: next })}
            />
          </div>
        </div>

        {/* footer */}
        <div className="flex items-center justify-between border-t border-edge px-4 py-3">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClone}
              className="inline-flex h-8 items-center gap-1 rounded-md border border-edge px-2.5 text-xs text-text-muted hover:bg-surface-2"
            >
              <Copy className="h-3 w-3" /> {t("manage.entry.duplicate")}
            </button>
            <button
              type="button"
              onClick={onRemove}
              className="inline-flex h-8 items-center gap-1 rounded-md border border-critical/30 px-2.5 text-xs text-critical hover:bg-critical/10"
            >
              <Trash2 className="h-3 w-3" /> {t("manage.rules.delete")}
            </button>
            {lastRun && (
              <Link
                to={lastRunHistoricalHref(lastRun)}
                title={t("manage.entry.lastRunTitle", {
                  start: new Date(lastRun.start).toLocaleString(),
                  end: new Date(lastRun.end).toLocaleString(),
                  tz: timezone,
                })}
                className="inline-flex h-8 items-center gap-1 rounded-md border border-edge px-2.5 text-xs text-text-muted hover:bg-surface-2 hover:text-text"
              >
                <History className="h-3 w-3" /> {t("manage.active.viewLastRun")}
              </Link>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* N12 — the in-modal commit. Edits only persist on the page's
                Hold-to-save; surface that gesture here so the operator can
                apply without hunting behind the drawer. It saves the WHOLE
                draft (page wording), and the editor closes only after the
                async save resolves so the toast/edge echo isn't pre-empted.
                Compact h-7 so the footer doesn't grow on the TB iframe. */}
            {dirty && onSave && (
              <HoldToConfirm
                label={t("manage.schedule.holdToSave")}
                onConfirm={() => {
                  void onSave().then(onClose);
                }}
                className="h-7 px-2 text-[11px] normal-case tracking-normal"
              />
            )}
            {/* N12 — "Done" (accent + Check) impersonated a save it never
                delivered; demote to a neutral outlined "Close" with no
                check, so it never reads as persistence. */}
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-edge px-3 text-xs text-text-muted hover:bg-surface-2 hover:text-text"
            >
              {t("common.close")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="mb-1 text-[10px] uppercase tracking-wider text-text-faint font-semibold">
        {label}
      </div>
      {children}
      {hint && <div className="mt-1 text-[10px] text-text-faint">{hint}</div>}
    </label>
  );
}

function BoolToggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  const t = useT();
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={cn(
        "inline-flex h-8 items-center gap-1.5 rounded-md border px-3 text-xs",
        value
          ? "border-ok/40 bg-ok/10 text-ok"
          : "border-edge bg-surface text-text-muted hover:bg-surface-2",
      )}
    >
      {value ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
      {value ? t("manage.entry.enabled") : t("manage.entry.disabled")}
    </button>
  );
}

function TimeInput({
  value,
  onChange,
  allowEndOfDay,
}: {
  value: number;
  onChange: (v: number) => void;
  allowEndOfDay?: boolean;
}) {
  const isEndOfDay = value >= 1440;
  return (
    <div className="flex items-center gap-2">
      <input
        type="time"
        step={300}
        value={isEndOfDay ? "23:59" : fmtHm(value)}
        disabled={isEndOfDay}
        onChange={(e) => {
          const [hh, mm] = e.target.value.split(":").map((s) => parseInt(s, 10));
          if (!Number.isFinite(hh) || !Number.isFinite(mm)) return;
          onChange(hh * 60 + mm);
        }}
        className="text-input rounded-md border border-edge bg-surface px-2 py-1.5 font-mono text-sm disabled:opacity-60"
      />
      {allowEndOfDay && (
        <label className="inline-flex items-center gap-1 text-[11px] text-text-muted">
          <input
            type="checkbox"
            checked={isEndOfDay}
            onChange={(e) => onChange(e.target.checked ? 1440 : 23 * 60)}
            className="accent-accent"
          />
          24:00
        </label>
      )}
    </div>
  );
}
